import React from 'react';
import styled from 'styled-components';
import { AdminAPI } from '../../../api/admin';
import MediaPicker from '../components/MediaPicker';
import { normalizeMediaUrl } from '../../../api/base';

const Container = styled.div`
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
`;

const Button = styled.button`
  padding: 10px 16px;
  background: var(--primary);
  color: var(--primaryFg);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  
  &:hover {
    opacity: 0.9;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: var(--card);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: var(--shadow);
`;

const Th = styled.th`
  padding: 12px 16px;
  background: var(--bg);
  text-align: left;
  font-weight: 600;
  border-bottom: 1px solid var(--bdr);
`;

const Td = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid var(--bdr);
  vertical-align: top;
`;

const ServiceImage = styled.img`
  width: 80px;
  height: 60px;
  object-fit: cover;
  border-radius: 6px;
  background: #f5f5f5;
  border: 1px solid var(--bdr);
`;

const ServiceImagePlaceholder = styled.div`
  width: 80px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  border-radius: 6px;
  color: #999;
  font-size: 10px;
  text-align: center;
  border: 1px solid #e0e0e0;
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  margin-right: 8px;
  border: 1px solid var(--bdr);
  background: var(--card);
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  
  &:hover {
    background: var(--bg);
  }
  
  &.edit {
    color: #0066cc;
  }
  
  &.delete {
    color: #dc3545;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--card);
  padding: 24px;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--bdr);
  border-radius: 8px;
  background: var(--bg);
  color: var(--fg);
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--bdr);
  border-radius: 8px;
  background: var(--bg);
  color: var(--fg);
  min-height: 80px;
  resize: vertical;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const CancelButton = styled(Button)`
  background: var(--bg);
  color: var(--fg);
  border: 1px solid var(--bdr);
`;

const DeleteButton = styled(Button)`
  background: #dc3545;
  color: white;
`;

const StatusToggle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
`;

const StatusIndicator = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 2px solid ${props => props.active ? '#28a745' : '#dc3545'};
  background: ${props => props.active ? '#28a745' : 'transparent'};
  position: relative;
  transition: all 0.2s ease;
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 6px;
    height: 6px;
    border-radius: 2px;
    background: white;
    opacity: ${props => props.active ? 1 : 0};
    transition: opacity 0.2s ease;
  }
`;

const ConfirmModal = styled(ModalContent)`
  max-width: 400px;
  text-align: center;
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  border: 1px solid #f5c6cb;
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
`;

const CustomCheckbox = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid ${props => props.checked ? '#28a745' : '#ccc'};
  border-radius: 4px;
  background: ${props => props.checked ? '#28a745' : 'transparent'};
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${props => props.checked ? '#1e7e34' : '#999'};
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 8px;
    height: 8px;
    border-radius: 2px;
    background: white;
    opacity: ${props => props.checked ? 1 : 0};
    transition: opacity 0.2s ease;
  }
`;

const CheckboxLabel = styled.label`
  font-weight: 600;
  cursor: pointer;
  user-select: none;
`;

// Функция для генерации slug из текста
function generateSlug(text) {
  if (!text) return '';
  
  // Транслитерация русских букв
  const translitMap = {
    'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'е': 'e', 'ё': 'e',
    'ж': 'zh', 'з': 'z', 'и': 'i', 'й': 'y', 'к': 'k', 'л': 'l', 'м': 'm',
    'н': 'n', 'о': 'o', 'п': 'p', 'р': 'r', 'с': 's', 'т': 't', 'у': 'u',
    'ф': 'f', 'х': 'h', 'ц': 'ts', 'ч': 'ch', 'ш': 'sh', 'щ': 'sch',
    'ъ': '', 'ы': 'y', 'ь': '', 'э': 'e', 'ю': 'yu', 'я': 'ya'
  };
  
  return text
    .toLowerCase()
    .split('')
    .map(char => translitMap[char] || char)
    .join('')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-+/g, '-');
}

// Функция для получения URL изображения услуги
function getServiceImageUrl(service) {
  // Приоритет 1: cover_url из API
  if (service.cover_url) {
    const normalizedUrl = normalizeMediaUrl(service.cover_url);
    if (normalizedUrl && normalizedUrl !== 'null' && normalizedUrl !== 'undefined') {
      return normalizedUrl;
    }
  }
  
  // Приоритет 2: media объект
  if (service.media && service.media.url) {
    const normalizedUrl = normalizeMediaUrl(service.media.url);
    if (normalizedUrl && normalizedUrl !== 'null' && normalizedUrl !== 'undefined') {
      return normalizedUrl;
    }
  }
  
  // Приоритет 3: cover поле (для обратной совместимости)
  if (service.cover) {
    const normalizedUrl = normalizeMediaUrl(service.cover);
    if (normalizedUrl && normalizedUrl !== 'null' && normalizedUrl !== 'undefined' && !service.cover.includes('/static/')) {
      return normalizedUrl;
    }
  }
  
  return null;
}

export default function ServicesTab() {
  const [services, setServices] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showModal, setShowModal] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [editingService, setEditingService] = React.useState(null);
  const [deletingService, setDeletingService] = React.useState(null);
  const [error, setError] = React.useState('');
  const [formData, setFormData] = React.useState({
    title_ru: '',
    title_en: '',
    title_zh: '',
    short_description_ru: '',
    short_description_en: '',
    short_description_zh: '',
    description_ru: '',
    description_en: '',
    description_zh: '',
    icon: '',
    link: '',
    media_id: null, // Изменено с cover на media_id
    is_active: true,
    sort_order: 1
  });

  const loadServices = React.useCallback(async () => {
    try {
      setLoading(true);
      const data = await AdminAPI.listServices();
      // Отладочная информация
      setServices(data.services || []);
    } catch (error) {
      console.error('Failed to load services:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadServices();
  }, [loadServices]);

  const handleCreate = () => {
    setEditingService(null);
    setFormData({
      title_ru: '',
      title_en: '',
      short_description_ru: '',
      short_description_en: '',
      description_ru: '',
      description_en: '',
      icon: '',
      link: '',
      media_id: null, // Изменено с cover на media_id
      is_active: true,
      sort_order: services.length + 1
    });
    setShowModal(true);
  };

  const handleEdit = (service) => {
    // Отладочная информация
    setEditingService(service);
    setFormData({
      title_ru: service.title_ru || '',
      title_en: service.title_en || '',
      title_zh: service.title_zh || '',
      short_description_ru: service.short_description_ru || '',
      short_description_en: service.short_description_en || '',
      short_description_zh: service.short_description_zh || '',
      description_ru: service.description_ru || '',
      description_en: service.description_en || '',
      description_zh: service.description_zh || '',
      icon: service.icon || '',
      link: service.link || '',
      media_id: service.media_id || null, // Изменено с cover на media_id
      is_active: service.is_active !== false,
      sort_order: service.sort_order || 1
    });
    // Отладочная информация
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      // Отладочная информация
      if (editingService) {
        await AdminAPI.updateService(editingService.id, formData);
      } else {
        await AdminAPI.createService(formData);
      }
      setShowModal(false);
      loadServices();
      
      // Отправляем событие обновления услуг для других компонентов
      window.dispatchEvent(new CustomEvent('servicesUpdated', {
        detail: { action: editingService ? 'update' : 'create', serviceId: editingService?.id }
      }));
      } catch (error) {
      console.error('Failed to save service:', error);
      // Показываем ошибку через состояние вместо alert
      setError('Ошибка при сохранении услуги');
    }
  };

  const handleDeleteClick = (service) => {
    setDeletingService(service);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingService) return;
    
    try {
      await AdminAPI.deleteService(deletingService.id);
      setShowDeleteModal(false);
      setDeletingService(null);
      loadServices();
      
      // Отправляем событие обновления услуг для других компонентов
      window.dispatchEvent(new CustomEvent('servicesUpdated', {
        detail: { action: 'delete', serviceId: deletingService.id }
      }));
      } catch (error) {
      console.error('Failed to delete service:', error);
      setError('Ошибка при удалении услуги');
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeletingService(null);
  };

  const handleInputChange = (field, value) => {
    // Отладочная информация
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Автоматически генерируем ссылку при изменении названия (только если ссылка пустая или не была изменена вручную)
      if (field === 'title_ru' && value && (!prev.link || prev.link === '' || prev.link.startsWith('/services/'))) {
        const slug = generateSlug(value);
        updated.link = `/services/${slug}`;
      }
      
      return updated;
    });
  };

  if (loading) {
    return <Container>Загрузка...</Container>;
  }

  return (
    <Container>
      <Header>
        <Title>Управление услугами</Title>
        <Button onClick={handleCreate}>Добавить услугу</Button>
      </Header>

      {error && (
        <ErrorMessage>
          {error}
          <button 
            onClick={() => setError('')}
            style={{ 
              float: 'right', 
              background: 'none', 
              border: 'none', 
              color: 'inherit', 
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            ×
          </button>
        </ErrorMessage>
      )}

      <Table>
        <thead>
          <tr>
            <Th>Изображение</Th>
            <Th>Название</Th>
            <Th>Анонс</Th>
            <Th>Ссылка</Th>
            <Th>Порядок</Th>
            <Th>Статус</Th>
            <Th>Действия</Th>
          </tr>
        </thead>
        <tbody>
          {services.map((service) => {
            const imageUrl = getServiceImageUrl(service);
            // Отладочная информация
            
            return (
              <tr key={service.id}>
                <Td>
                  {imageUrl ? (
                    <ServiceImage 
                      src={imageUrl} 
                      alt={service.title_ru || service.title_en || 'Service'} 
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                      onLoad={(e) => {
                        }}
                    />
                  ) : null}
                  <ServiceImagePlaceholder style={{ display: imageUrl ? 'none' : 'flex' }}>
                    Нет фото
                  </ServiceImagePlaceholder>
                </Td>
              <Td>
                <strong>{service.title_ru}</strong>
                {service.title_en && (
                  <div style={{ fontSize: '12px', opacity: 0.7 }}>
                    {service.title_en}
                  </div>
                )}
              </Td>
              <Td>
                <div style={{ maxWidth: '200px', fontSize: '14px' }}>
                  {service.short_description_ru || <span style={{ opacity: 0.5, fontStyle: 'italic' }}>Анонс не заполнен</span>}
                </div>
              </Td>
              <Td>
                <code style={{ fontSize: '12px' }}>{service.link}</code>
              </Td>
              <Td>{service.sort_order}</Td>
              <Td>
                <StatusToggle>
                  <StatusIndicator active={service.is_active} />
                  <span style={{ color: service.is_active ? '#28a745' : '#dc3545' }}>
                    {service.is_active ? 'Активна' : 'Неактивна'}
                  </span>
                </StatusToggle>
              </Td>
              <Td>
                <ActionButton className="edit" onClick={() => handleEdit(service)}>
                  Редактировать
                </ActionButton>
                <ActionButton className="delete" onClick={() => handleDeleteClick(service)}>
                  Удалить
                </ActionButton>
              </Td>
            </tr>
            );
          })}
        </tbody>
      </Table>

      {/* Модальное окно редактирования */}
      {showModal && (
        <Modal onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <ModalContent>
            <h3>{editingService ? 'Редактировать услугу' : 'Добавить услугу'}</h3>
            
            <FormGroup>
              <Label>Название (RU)</Label>
              <Input
                value={formData.title_ru}
                onChange={(e) => handleInputChange('title_ru', e.target.value)}
                placeholder="Название услуги на русском"
              />
            </FormGroup>

            <FormGroup>
              <Label>Название (EN)</Label>
              <Input
                value={formData.title_en}
                onChange={(e) => handleInputChange('title_en', e.target.value)}
                placeholder="Service name in English"
              />
            </FormGroup>

            <FormGroup>
              <Label>Название (ZH)</Label>
              <Input
                value={formData.title_zh}
                onChange={(e) => handleInputChange('title_zh', e.target.value)}
                placeholder="服务名称"
              />
            </FormGroup>

            <FormGroup>
              <Label>Анонс (RU) - отображается на карточках</Label>
              <Textarea
                value={formData.short_description_ru}
                onChange={(e) => handleInputChange('short_description_ru', e.target.value)}
                placeholder="Краткое описание для карточки услуги"
                style={{ minHeight: '60px' }}
              />
            </FormGroup>

            <FormGroup>
              <Label>Анонс (EN) - отображается на карточках</Label>
              <Textarea
                value={formData.short_description_en}
                onChange={(e) => handleInputChange('short_description_en', e.target.value)}
                placeholder="Short description for service card"
                style={{ minHeight: '60px' }}
              />
            </FormGroup>

            <FormGroup>
              <Label>Анонс (ZH) - отображается на карточках</Label>
              <Textarea
                value={formData.short_description_zh}
                onChange={(e) => handleInputChange('short_description_zh', e.target.value)}
                placeholder="服务简介"
                style={{ minHeight: '60px' }}
              />
            </FormGroup>

            <FormGroup>
              <Label>Полное описание (RU) - отображается на странице услуги</Label>
              <Textarea
                value={formData.description_ru}
                onChange={(e) => handleInputChange('description_ru', e.target.value)}
                placeholder="Полное описание услуги на русском"
                style={{ minHeight: '120px' }}
              />
            </FormGroup>

            <FormGroup>
              <Label>Полное описание (EN) - отображается на странице услуги</Label>
              <Textarea
                value={formData.description_en}
                onChange={(e) => handleInputChange('description_en', e.target.value)}
                placeholder="Full service description in English"
                style={{ minHeight: '120px' }}
              />
            </FormGroup>

            <FormGroup>
              <Label>Полное описание (ZH) - отображается на странице услуги</Label>
              <Textarea
                value={formData.description_zh}
                onChange={(e) => handleInputChange('description_zh', e.target.value)}
                placeholder="完整服务描述"
                style={{ minHeight: '120px' }}
              />
            </FormGroup>

            <FormGroup>
              <Label>Ссылка</Label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <Input
                  value={formData.link}
                  onChange={(e) => handleInputChange('link', e.target.value)}
                  placeholder="/services/example"
                  style={{ flex: 1 }}
                />
                <Button
                  type="button"
                  onClick={() => {
                    const slug = generateSlug(formData.title_ru || formData.title_en);
                    if (slug) {
                      handleInputChange('link', `/services/${slug}`);
                    }
                  }}
                  style={{ 
                    padding: '10px 12px',
                    fontSize: '12px',
                    whiteSpace: 'nowrap',
                    background: 'var(--bg)',
                    color: 'var(--fg)',
                    border: '1px solid var(--bdr)'
                  }}
                >
                  Сгенерировать
                </Button>
              </div>
              <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '4px' }}>
                Ссылка генерируется автоматически при вводе названия
              </div>
            </FormGroup>

            <FormGroup>
              <MediaPicker
                value={formData.media_id}
                onChange={(mediaId) => handleInputChange('media_id', mediaId)}
                label="Изображение услуги"
              />
            </FormGroup>

            <FormGroup>
              <Label>Иконка</Label>
              <Input
                value={formData.icon}
                onChange={(e) => handleInputChange('icon', e.target.value)}
                placeholder="investment, legal, design, analytics"
              />
            </FormGroup>

            <FormGroup>
              <Label>Порядок сортировки</Label>
              <Input
                type="number"
                value={formData.sort_order}
                onChange={(e) => handleInputChange('sort_order', parseInt(e.target.value) || 1)}
                min="1"
              />
            </FormGroup>

            <FormGroup>
              <CheckboxWrapper>
                <CustomCheckbox 
                  checked={formData.is_active}
                  onClick={() => handleInputChange('is_active', !formData.is_active)}
                />
                <CheckboxLabel onClick={() => handleInputChange('is_active', !formData.is_active)}>
                  Активна
                </CheckboxLabel>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  style={{ display: 'none' }}
                />
              </CheckboxWrapper>
            </FormGroup>

            <ButtonGroup>
              <CancelButton onClick={() => setShowModal(false)}>
                Отмена
              </CancelButton>
              <Button onClick={handleSave}>
                {editingService ? 'Сохранить' : 'Создать'}
              </Button>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}

      {/* Модальное окно подтверждения удаления */}
      {showDeleteModal && deletingService && (
        <Modal onClick={(e) => e.target === e.currentTarget && handleDeleteCancel()}>
          <ConfirmModal>
            <h3>Подтверждение удаления</h3>
            <p>Вы уверены, что хотите удалить услугу "{deletingService.title_ru}"?</p>
            <p style={{ fontSize: '14px', opacity: 0.7 }}>
              Это действие нельзя отменить.
            </p>
            
            <ButtonGroup>
              <CancelButton onClick={handleDeleteCancel}>
                Отмена
              </CancelButton>
              <DeleteButton onClick={handleDeleteConfirm}>
                Удалить
              </DeleteButton>
            </ButtonGroup>
          </ConfirmModal>
        </Modal>
      )}
    </Container>
  );
}